import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Link, Navigate, useParams } from 'react-router';
import { api } from '../api.js';
import LockForeverToggle from '../components/LockForeverToggle.js';

/**
 * Organizer-only: the football bolão's own settings, reached via the gear
 * icon `PoolNav` shows next to the title. Just `allowLockForever` today —
 * structured as its own screen (rather than folded into `PoolScreen` or the
 * creation form) so a second setting has somewhere to go without a redesign.
 *
 * Custom bolões have no equivalent screen: their lock mechanic is already
 * per-question (`allowLockForever` on each question, set while authoring
 * it), so there is nothing bolão-wide to toggle here for one.
 */
export default function BolaoSettingsScreen() {
  const { t } = useTranslation();
  const { id = '', poolId = '' } = useParams();
  const queryClient = useQueryClient();

  const {
    data: pool,
    isPending,
    isError,
  } = useQuery({ queryKey: ['pool', id, poolId], queryFn: () => api.getPool(id, poolId) });

  const [allowLockForever, setAllowLockForever] = useState(true);
  // Seeds the toggle from the loaded value exactly once — an unconditional
  // sync would clobber whatever the organizer is mid-edit the moment a
  // background refetch lands.
  const [seeded, setSeeded] = useState(false);
  useEffect(() => {
    if (pool && !seeded) {
      setAllowLockForever(pool.allowLockForever);
      setSeeded(true);
    }
  }, [pool, seeded]);

  const save = useMutation({
    mutationFn: () => api.updateBolaoSettings(id, poolId, { allowLockForever }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['pool', id, poolId] }),
  });

  if (isPending) {
    return (
      <main className="screen">
        <p className="muted">{t('common.loading')}</p>
      </main>
    );
  }

  if (isError || !pool) {
    return (
      <main className="screen">
        <Link to={`/groups/${id}`} className="muted">
          {t('common.back')}
        </Link>
        <p className="error">{t('common.somethingWentWrong')}</p>
      </main>
    );
  }

  // The server enforces this independently — this just keeps a
  // non-organizer who lands here (a stale link, typing the URL by hand)
  // from seeing a form that would 403 the moment they touched it.
  if (!pool.isOrganizer) {
    return <Navigate to={`/groups/${id}/bolaos/${poolId}/predictions`} replace />;
  }

  return (
    <main className="screen">
      <Link to={`/groups/${id}/bolaos/${poolId}/predictions`} className="muted">
        {t('common.back')}
      </Link>
      <h1>{t('poolSettings.title')}</h1>
      <p className="muted">{pool.name}</p>

      <form
        className="panel stack"
        style={{ padding: 'var(--space-4)' }}
        onSubmit={(event) => {
          event.preventDefault();
          if (!save.isPending) save.mutate();
        }}
      >
        <LockForeverToggle
          checked={allowLockForever}
          onChange={setAllowLockForever}
          disabled={save.isPending}
        />

        {save.isError ? <p className="error">{t('common.somethingWentWrong')}</p> : null}
        {save.isSuccess ? <p className="muted">{t('poolSettings.saved')}</p> : null}

        <button type="submit" className="button button-primary" disabled={save.isPending}>
          {save.isPending ? t('poolSettings.saving') : t('poolSettings.save')}
        </button>
      </form>
    </main>
  );
}
